/**
 * Splits a streamed model response into text (chat-bound) and artifact (canvas-bound)
 * chunks. The model is instructed (system-prompt.md) to wrap the artifact in
 * `<artifact>...</artifact>` at the top level. SSE deltas may split tag boundaries,
 * so we keep up to `target.length - 1` characters in a buffer between flushes.
 */

export const OPEN_TAG = '<artifact>';
export const CLOSE_TAG = '</artifact>';

export type ExtractorEvent =
  | { type: 'text'; chunk: string }
  | { type: 'artifact'; chunk: string };

export class ArtifactExtractor {
  private buffer = '';
  private inArtifact = false;
  private allText = '';
  private allArtifact = '';

  /** Returns whether the model emitted an `<artifact>` tag at all. */
  hasArtifact(): boolean {
    return this.allArtifact.length > 0 || this.inArtifact;
  }

  text(): string {
    return this.allText;
  }

  artifact(): string {
    return this.allArtifact;
  }

  push(chunk: string): ExtractorEvent[] {
    this.buffer += chunk;
    const events: ExtractorEvent[] = [];

    for (;;) {
      const target = this.inArtifact ? CLOSE_TAG : OPEN_TAG;
      const idx = this.buffer.indexOf(target);
      if (idx === -1) {
        const keep = target.length - 1;
        if (this.buffer.length > keep) {
          const emit = this.buffer.slice(0, this.buffer.length - keep);
          if (emit) {
            const ev: ExtractorEvent = this.inArtifact
              ? { type: 'artifact', chunk: emit }
              : { type: 'text', chunk: emit };
            this.record(ev);
            events.push(ev);
          }
          this.buffer = this.buffer.slice(this.buffer.length - keep);
        }
        break;
      }
      if (idx > 0) {
        const emit = this.buffer.slice(0, idx);
        const ev: ExtractorEvent = this.inArtifact
          ? { type: 'artifact', chunk: emit }
          : { type: 'text', chunk: emit };
        this.record(ev);
        events.push(ev);
      }
      this.buffer = this.buffer.slice(idx + target.length);
      this.inArtifact = !this.inArtifact;
    }

    return events;
  }

  flush(): ExtractorEvent[] {
    if (!this.buffer) return [];
    const ev: ExtractorEvent = this.inArtifact
      ? { type: 'artifact', chunk: this.buffer }
      : { type: 'text', chunk: this.buffer };
    this.buffer = '';
    this.record(ev);
    return [ev];
  }

  private record(ev: ExtractorEvent): void {
    if (ev.type === 'text') this.allText += ev.chunk;
    else this.allArtifact += ev.chunk;
  }
}
